// Sentry v8 folded @sentry/types and @sentry/tracing into the SDK; main is
// still on v7 where they are separate packages. Types come from
// @sentry/core here. #4166
import type { Event } from "@sentry/core";

const REDACTED = "[Filtered]";
const UNKNOWN_ERROR = "Unknown error";
const STRUCTURED_DIAGNOSTIC_OMITTED = "Structured diagnostic omitted";
const MAX_STRING_LENGTH = 500;
const MAX_ARRAY_LENGTH = 20;
const MAX_DIAGNOSTIC_REASON_LENGTH = 160;
const MAX_ALERT_IDENTITY_LENGTH = 160;

type SanitizedValue =
  | null
  | undefined
  | string
  | number
  | boolean
  | SanitizedValue[]
  | { [key: string]: SanitizedValue };

const ALERT_FIELD_ALLOWLIST = new Set(
  `cause cartVersion chunkType errorCode franchise franchiseId itemCount itemId key
   modal networkError offerId offerType operationCount order_type orderType
   packageOfferId page rateLimited restaurantId retryCount status statusCode truncated`
    .trim()
    .split(/\s+/)
);

const SENSITIVE_KEYS = new Set(
  `address apartment apikey authorization body building card cardholder cardnumber
   cookie cookies customerid cvc cvv data email error errorinfo forwarded href ip
   ipaddress mobile pan password payload phone pin query querystring request response
   search secret session setcookie stack street token url userid xforwardedfor xrealip`
    .trim()
    .split(/\s+/)
);

const SENSITIVE_NAME_KEYS = new Set(
  `cardholdername contactname customername displayname firstname fullname lastname
   recipientname username`
    .trim()
    .split(/\s+/)
);
const SENSITIVE_IDENTITY_CONTAINERS = new Set(
  "cardholder contact customer profile recipient user".split(" ")
);

const normalizeKey = (key: string) =>
  key.toLowerCase().replace(/[^a-z\d]/g, "");

const isSensitiveKey = (key: string, parentKey?: string) => {
  const normalizedKey = normalizeKey(key);
  const hasSensitiveIdentityParent =
    parentKey && SENSITIVE_IDENTITY_CONTAINERS.has(normalizeKey(parentKey));

  return (
    SENSITIVE_KEYS.has(normalizedKey) ||
    SENSITIVE_NAME_KEYS.has(normalizedKey) ||
    normalizedKey.startsWith("card") ||
    normalizedKey.endsWith("address") ||
    normalizedKey.endsWith("cardnumber") ||
    normalizedKey.endsWith("email") ||
    normalizedKey.endsWith("mobile") ||
    normalizedKey.endsWith("phone") ||
    normalizedKey.endsWith("token") ||
    normalizedKey.endsWith("password") ||
    normalizedKey.endsWith("secret") ||
    (Boolean(hasSensitiveIdentityParent) &&
      ["id", "name"].includes(normalizedKey))
  );
};

const stripQueryString = (value: string) =>
  value.replace(/([a-z][a-z\d+.-]*:\/\/[^\s?#]+|\/[^\s?#]*)\?[^\s#]*/gi, "$1");

const isPlausibleEpochMilliseconds = (digits: string) =>
  digits.length === 13 &&
  Number(digits) >= 946684800000 &&
  Number(digits) <= 4102444800000;

const passesLuhnCheck = (digits: string) => {
  let sum = 0;
  let doubleDigit = false;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = Number(digits[index]);
    if (doubleDigit) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    sum += digit;
    doubleDigit = !doubleDigit;
  }

  return sum % 10 === 0;
};

const redactCardNumbers = (value: string) =>
  value.replace(/\b(?:\d[ -]*){12,18}\d\b/g, (candidate) => {
    const digits = candidate.replace(/\D/g, "");
    if (isPlausibleEpochMilliseconds(digits)) {
      return candidate;
    }

    return passesLuhnCheck(digits) ? REDACTED : candidate;
  });

export const sanitizeString = (value: string) =>
  redactCardNumbers(
    stripQueryString(value)
      .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, REDACTED)
      .replace(/\bBearer\s+[A-Za-z\d._~+/-]+=*/gi, "Bearer [Filtered]")
      .replace(
        /\beyJ[A-Za-z\d_-]{5,}\.[A-Za-z\d_-]{5,}\.[A-Za-z\d_-]*\b/g,
        REDACTED
      )
  )
    .replace(/\+\d[\d ().-]{7,}\d/g, REDACTED)
    .replace(
      /\b((?:(?:first|last|full|customer)\s*)?name)\s*[:=]\s*[^,;\n]+/gi,
      "$1=[Filtered]"
    )
    .replace(
      /\b(token|password|secret|api[-_ ]?key|email|phone|card(?:number)?|customer(?:id)?)\s*[:=]\s*[^\s,;]+/gi,
      "$1=[Filtered]"
    )
    .slice(0, MAX_STRING_LENGTH);

const sanitizeAlertString = (value: string) => {
  const trimmed = value.trim();
  const stateMarker = trimmed.search(/(?:,?\s*cart actions\s*:|[{[])/i);
  const plainText =
    stateMarker >= 0 ? trimmed.slice(0, stateMarker).trim() : trimmed;

  if (!plainText || /^["'].*["']$/.test(plainText)) {
    return STRUCTURED_DIAGNOSTIC_OMITTED;
  }

  return sanitizeString(plainText).slice(0, MAX_DIAGNOSTIC_REASON_LENGTH);
};

export const sanitizeAlertIdentity = (text: string): string =>
  `Client diagnostic: ${sanitizeAlertString(text)}`.slice(
    0,
    MAX_ALERT_IDENTITY_LENGTH
  );

export const sanitizeValue = (
  value: unknown,
  seen = new WeakSet<object>(),
  depth = 0,
  parentKey?: string
): SanitizedValue => {
  if (
    value == null ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  if (typeof value === "string") {
    return sanitizeString(value);
  }
  if (value instanceof Error) {
    return { name: value.name };
  }
  if (typeof value !== "object" || depth >= 4) {
    return REDACTED;
  }
  if (seen.has(value)) {
    return "[Circular]";
  }

  seen.add(value);
  try {
    if (Array.isArray(value)) {
      return value
        .slice(0, MAX_ARRAY_LENGTH)
        .map((entry) => sanitizeValue(entry, seen, depth + 1, parentKey));
    }

    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        key,
        isSensitiveKey(key, parentKey)
          ? REDACTED
          : sanitizeValue(entry, seen, depth + 1, key),
      ])
    );
  } finally {
    seen.delete(value);
  }
};

export const sanitizeDiagnosticReason = (error: unknown): string => {
  let reason = UNKNOWN_ERROR;
  if (error instanceof Error) {
    reason = error.message;
  } else if (typeof error === "string") {
    reason = error;
  } else if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    reason = error.message;
  }

  return sanitizeAlertString(reason);
};

export const sanitizeAlertData = (data: unknown): SanitizedValue => {
  if (typeof data === "string") {
    return { cause: sanitizeAlertString(data) };
  }
  if (Array.isArray(data)) {
    return {
      itemCount: Math.min(data.length, MAX_ARRAY_LENGTH),
      truncated: data.length > MAX_ARRAY_LENGTH,
    };
  }
  if (!data || typeof data !== "object") {
    return {};
  }

  const sourceEntries = Object.entries(data);
  const sanitizedEntries = sourceEntries
    .filter(
      ([key, value]) =>
        ALERT_FIELD_ALLOWLIST.has(key) &&
        value !== undefined &&
        (key === "cause" ||
          value === null ||
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean")
    )
    .map(([key, value]) => [
      key,
      key === "cause"
        ? sanitizeDiagnosticReason(value)
        : typeof value === "string"
        ? sanitizeAlertString(value)
        : value,
    ]);

  return sanitizedEntries.length > 0
    ? Object.fromEntries(sanitizedEntries)
    : sourceEntries.length > 0
    ? { cause: STRUCTURED_DIAGNOSTIC_OMITTED }
    : {};
};

const sanitizeStandardContext = (context: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(context).map(([key, value]) => [
      key,
      isSensitiveKey(key)
        ? REDACTED
        : sanitizeValue(value, new WeakSet(), 0, key),
    ])
  );

export const sanitizeSentryContexts = (
  contexts: Event["contexts"]
): Event["contexts"] =>
  Object.fromEntries(
    Object.entries(contexts ?? {}).map(([key, context]) => [
      key,
      (key === "response" || key === "trace") &&
      context &&
      typeof context === "object" &&
      !Array.isArray(context)
        ? sanitizeStandardContext(context)
        : sanitizeValue(context, new WeakSet(), 0, key),
    ])
  ) as Event["contexts"];
